import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Play, Eye, Check, X, Trash2, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FREQUENCIES = [['monthly', 'Monthly'], ['biweekly', 'Every 2 weeks'], ['weekly', 'Weekly']];

const STATUS_STYLE = {
  ok: 'bg-green-100 text-green-700', awaiting_approval: 'bg-amber-100 text-amber-700', preview: 'bg-blue-100 text-blue-700',
  running: 'bg-blue-100 text-blue-700', skipped: 'bg-gray-100 text-gray-600', rejected: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};
const STATUS_LABEL = {
  ok: 'Published', awaiting_approval: 'Awaiting approval', preview: 'Preview', running: 'Running',
  skipped: 'Nothing posted', rejected: 'Rejected', failed: 'Failed',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status] || 'bg-gray-100 text-gray-600'}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

// Pools are edited as one line per item; origins as "Country | Port | INCOTERM".
const toLines = (arr) => (arr || []).join('\n');
const fromLines = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
const originsToText = (origins) => (origins || []).map(o => `${o.country} | ${o.port} | ${o.incoterm}`).join('\n');
const textToOrigins = (text) => fromLines(text).map(line => {
  const [country = '', port = '', incoterm = 'FOB'] = line.split('|').map(s => s.trim());
  return { country, port, incoterm: incoterm || 'FOB' };
}).filter(o => o.country && o.port);

// Form <-> API shape. Pools live as text in the form while editing.
const toForm = (settings) => ({
  ...settings,
  payment_terms: toLines(settings.payment_terms),
  shipment_windows: toLines(settings.shipment_windows),
  commodities: Object.fromEntries(Object.entries(settings.commodities).map(([k, c]) => [k, {
    ...c, product_names: toLines(c.product_names), origins: originsToText(c.origins), specs: toLines(c.specs), packaging: toLines(c.packaging),
  }])),
});
const toPayload = (form) => ({
  ...form,
  payment_terms: fromLines(form.payment_terms),
  shipment_windows: fromLines(form.shipment_windows),
  commodities: Object.fromEntries(Object.entries(form.commodities).map(([k, c]) => [k, {
    ...c, product_names: fromLines(c.product_names), origins: textToOrigins(c.origins), specs: fromLines(c.specs), packaging: fromLines(c.packaging),
    reference_price: c.reference_price === '' || c.reference_price == null ? null : Number(c.reference_price),
  }])),
});

const input = 'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none';

const OffersTab = ({ primaryColor }) => {
  const [overview, setOverview] = useState(null);
  const [form, setForm] = useState(null);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [openCommodity, setOpenCommodity] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (resetForm = false) => {
    try {
      const [o, r] = await Promise.all([
        api.get('/admin/posting/offers'),
        api.get('/admin/posting/runs', { params: { kind: 'monthly_offers', limit: 12 } }),
      ]);
      setOverview(o.data);
      setRuns(r.data.runs || []);
      setForm(prev => (resetForm || !prev ? toForm(o.data.settings) : prev));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load offer settings');
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  const running = overview?.running || starting;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(async () => { await load(false); setStarting(false); }, 4000);
    return () => clearInterval(id);
  }, [running, load]);

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const setCommodity = (key, patch) => setForm(prev => ({ ...prev, commodities: { ...prev.commodities, [key]: { ...prev.commodities[key], ...patch } } }));
  const dirty = form && overview && JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(toForm(overview.settings)));

  const save = async () => {
    try {
      setSaving(true);
      await api.put('/admin/posting/offers', toPayload(form));
      toast.success('Settings saved');
      await load(true);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save — check the fields');
    } finally {
      setSaving(false);
    }
  };

  const startRun = async (action) => {
    if (dirty) { toast.error('Save your settings first so the run uses them'); return; }
    if (action === 'run' && form.mode === 'auto' &&
        !window.confirm('This will post the offers to the marketplace now' +
          (form.destinations?.email ? ` and email ${overview.email_audience} users` : '') + '. Continue?')) return;
    try {
      setStarting(true);
      await api.post('/admin/posting/offers/run', { action });
      toast.success(action === 'preview' ? 'Preview started — takes a couple of minutes' : 'Run started — takes a couple of minutes');
    } catch (error) {
      setStarting(false);
      toast.error(error.response?.data?.detail || 'Could not start the run');
    }
  };

  const approve = async (run) => {
    const mail = form.destinations?.email ? ` and email ${overview.email_audience} users` : '';
    if (!window.confirm(`Post ${run.offers.length} offer(s) to the marketplace as ${overview.company_name}${mail}? Previous offers for these commodities will be retired.`)) return;
    try {
      setBusy('approve');
      await api.post(`/admin/posting/runs/${run._id}/approve`);
      toast.success('Offers posted');
      await load(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not post');
    } finally {
      setBusy('');
    }
  };

  const sendTest = async (runId) => {
    try {
      setBusy('test');
      const res = await api.post('/admin/posting/offers/send-test', { run_id: runId });
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the test');
    } finally {
      setBusy('');
    }
  };

  const reject = async (run) => {
    try { await api.post(`/admin/posting/runs/${run._id}/reject`); toast.success('Draft rejected'); await load(false); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not reject'); }
  };

  const saveOffer = async () => {
    try {
      await api.put(`/admin/posting/runs/${editing.runId}/offers/${editing.index}`,
        { title: editing.title, price: editing.price, description: editing.description });
      setEditing(null);
      toast.success('Offer updated');
      await load(false);
    } catch (error) { toast.error('Could not save the offer'); }
  };

  const removeOffer = async (runId, index) => {
    try { await api.delete(`/admin/posting/runs/${runId}/offers/${index}`); await load(false); }
    catch (error) { toast.error('Could not remove the offer'); }
  };

  if (!form || !overview) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const pending = runs.find(r => ['awaiting_approval', 'preview'].includes(r.status) && !r.published_at && r.offers?.length);
  const dow = form.frequency !== 'monthly';

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className="bg-white border rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" className="sr-only peer" checked={form.enabled} onChange={e => set({ enabled: e.target.checked })} aria-label="Enable monthly offers" />
          <span className="w-11 h-6 rounded-full bg-gray-300 peer-checked:bg-green-500 relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-5" />
          <span className="text-sm font-medium text-gray-800">{form.enabled ? 'Offers posting is on' : 'Offers posting is off'}</span>
        </label>
        <div className="text-sm text-gray-600 space-y-0.5 sm:text-right">
          <div>Posting as: <strong>{overview.company_name || 'company not found'}</strong> · {overview.live_offers} live offers</div>
          <div>Next run: <strong>{overview.settings.enabled && overview.next_run ? fmt(overview.next_run) : 'not scheduled'}</strong></div>
          <div>Last run: {overview.last_run ? <><StatusBadge status={overview.last_run.status} /> <span className="text-gray-500">{fmt(overview.last_run.started_at)}</span></> : 'never'}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => startRun('preview')} disabled={running} className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50">
          <Eye className="w-4 h-4" /> Preview (posts nothing)
        </button>
        <button onClick={() => startRun('run')} disabled={running} className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
          <Play className="w-4 h-4" /> Run now ({form.mode === 'auto' ? 'posts live' : 'saves a draft'})
        </button>
        {running && <span className="flex items-center gap-2 text-sm text-blue-700"><Loader2 className="w-4 h-4 animate-spin" /> Looking up market prices and building offers…</span>}
      </div>

      {/* Draft / preview review */}
      {pending && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-900">{pending.status === 'preview' ? 'Preview' : 'Waiting for your approval'} — {pending.offers.length} offer(s)</h3>
              <p className="text-sm text-gray-600">Nothing is posted until you approve. Check each price against the reference before you do.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => sendTest(pending._id)} disabled={busy === 'test'} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border hover:bg-gray-50 disabled:opacity-50">
                {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test email to me
              </button>
              <button onClick={() => reject(pending)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border text-red-600 hover:bg-red-50"><X className="w-4 h-4" /> Reject</button>
              <button onClick={() => approve(pending)} disabled={busy === 'approve'} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve &amp; post
              </button>
            </div>
          </div>
          {pending.offers.map((o, i) => (
            <div key={i} className="bg-white border rounded-lg p-4">
              {editing && editing.runId === pending._id && editing.index === i ? (
                <div className="space-y-2">
                  <input className={input} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} aria-label="Title" />
                  <input className={input} value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} aria-label="Price" />
                  <textarea className={input} rows={6} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} aria-label="Description" />
                  <div className="flex gap-2">
                    <button onClick={saveOffer} className="px-3 py-1.5 text-sm rounded-lg text-white" style={{ backgroundColor: primaryColor }}>Save</button>
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm rounded-lg bg-gray-100">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{o.title}</h4>
                      <div className="text-sm font-medium text-green-700 mt-0.5">{o.price}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setEditing({ runId: pending._id, index: i, title: o.title, price: o.price, description: o.description })} className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200">Edit</button>
                      <button onClick={() => removeOffer(pending._id, i)} className="p-1 text-gray-400 hover:text-red-600" aria-label="Remove offer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">{o.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Market reference {money(o.audit?.reference_price)}/MT · discount {o.audit?.discount_pct}% · open {o.start_date} to {o.expiration_date}
                    <span className="text-gray-400"> (reference and discount are shown here only, never on the public offer)</span>
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Settings */}
      <div className="bg-white border rounded-xl p-5 space-y-5">
        <h3 className="font-semibold text-gray-900">Settings</h3>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">When it goes live</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[['draft', 'Draft for my approval', 'Builds the offers and waits. Nothing is posted until you approve it here.'],
              ['auto', 'Auto-post', 'Posts straight to the marketplace. Only use once you trust the price lookup.']].map(([v, t, d]) => (
              <label key={v} className={`border rounded-lg p-3 cursor-pointer ${form.mode === v ? 'border-current bg-blue-50/40' : ''}`} style={form.mode === v ? { color: primaryColor } : {}}>
                <input type="radio" name="offers-mode" className="mr-2" checked={form.mode === v} onChange={() => set({ mode: v })} />
                <span className="font-medium text-gray-900">{t}</span>
                <p className="text-xs text-gray-500 mt-1 ml-5">{d}</p>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Frequency (the cron job checks hourly)</div>
          <div className="flex flex-wrap items-center gap-3">
            <select className="px-3 py-2 border rounded-lg text-sm" value={form.frequency} onChange={e => set({ frequency: e.target.value })} aria-label="Frequency">
              {FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {dow ? (
              <select className="px-3 py-2 border rounded-lg text-sm" value={form.schedule.day_of_week} onChange={e => set({ schedule: { ...form.schedule, day_of_week: Number(e.target.value) } })} aria-label="Day of week">
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            ) : (
              <select className="px-3 py-2 border rounded-lg text-sm" value={form.schedule.day_of_month} onChange={e => set({ schedule: { ...form.schedule, day_of_month: Number(e.target.value) } })} aria-label="Day of month">
                {Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={i + 1}>Day {i + 1}</option>)}
              </select>
            )}
            <select className="px-3 py-2 border rounded-lg text-sm" value={form.schedule.hour} onChange={e => set({ schedule: { ...form.schedule, hour: Number(e.target.value) } })} aria-label="Hour (UTC)">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-4 gap-4">
          <label className="block text-sm text-gray-700">Minimum discount to market (%)
            <input type="number" min="0" max="60" step="0.5" className={`${input} mt-1`} value={form.discount_min} onChange={e => set({ discount_min: Number(e.target.value) })} />
          </label>
          <label className="block text-sm text-gray-700">Maximum discount to market (%)
            <input type="number" min="0" max="60" step="0.5" className={`${input} mt-1`} value={form.discount_max} onChange={e => set({ discount_max: Number(e.target.value) })} />
          </label>
          <label className="block text-sm text-gray-700">Offers stay open (days)
            <input type="number" min="7" max="180" className={`${input} mt-1`} value={form.duration_days} onChange={e => set({ duration_days: Number(e.target.value) })} />
          </label>
          <label className="block text-sm text-gray-700">Posted by (company subdomain)
            <input className={`${input} mt-1`} value={form.company_subdomain} onChange={e => set({ company_subdomain: e.target.value })} />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-sm text-gray-700">Payment terms (one per line, one is picked per offer)
            <textarea className={`${input} mt-1`} rows={4} value={form.payment_terms} onChange={e => set({ payment_terms: e.target.value })} />
          </label>
          <label className="block text-sm text-gray-700">Shipment windows (one per line)
            <textarea className={`${input} mt-1`} rows={4} value={form.shipment_windows} onChange={e => set({ shipment_windows: e.target.value })} />
          </label>
        </div>

        <label className="block text-sm text-gray-700">Closing line added to every offer (how buyers should respond)
          <input className={`${input} mt-1`} value={form.contact_line} onChange={e => set({ contact_line: e.target.value })} />
        </label>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">When offers go live</div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.destinations?.email ?? true} onChange={e => set({ destinations: { ...form.destinations, email: e.target.checked } })} />
            Email all users with the offers and a link to the marketplace ({overview.email_audience} active, verified users; unsubscribed users are skipped)
          </label>
        </div>
        <label className="block text-sm text-gray-700">Email me if a run fails or is waiting for approval (blank = the default admin address)
          <input type="email" className={`${input} mt-1`} value={form.notify_email} onChange={e => set({ notify_email: e.target.value })} />
        </label>
      </div>

      {/* Commodities */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Commodities</h3>
        <p className="text-xs text-gray-500 mb-3">Each offer is priced between the minimum and maximum discount below the market reference. Origins, specifications and quantities are drawn from the lists, so each run differs from the last.</p>
        <div className="divide-y">
          {Object.entries(form.commodities).map(([key, c]) => (
            <div key={key} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={c.enabled} onChange={e => setCommodity(key, { enabled: e.target.checked })} aria-label={`Include ${c.label}`} />
                  <span className="font-medium text-gray-900">{c.label}</span>
                  <span className="text-xs text-gray-500">
                    {c.reference_price ? `reference ${money(c.reference_price)}/MT${c.reference_as_of ? ` (${c.reference_as_of})` : ''}` : 'no reference price yet'}
                  </span>
                </label>
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1 text-gray-600">Offers
                    <select className="px-2 py-1 border rounded" value={c.offers_per_run} onChange={e => setCommodity(key, { offers_per_run: Number(e.target.value) })}>
                      {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <button onClick={() => setOpenCommodity(openCommodity === key ? null : key)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900">
                    Edit {openCommodity === key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {openCommodity === key && (
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
                    <label className="block text-sm text-gray-700">Market price
                      <select className={`${input} mt-1`} value={c.price_mode} onChange={e => setCommodity(key, { price_mode: e.target.value })}>
                        <option value="auto">Look it up each run</option>
                        <option value="manual">I set it (USD per MT)</option>
                      </select>
                    </label>
                    <label className="block text-sm text-gray-700">{c.price_mode === 'manual' ? 'Reference price (USD/MT)' : 'Last looked-up reference (USD/MT)'}
                      <input type="number" min="0" step="1" className={`${input} mt-1`} value={c.reference_price ?? ''} disabled={c.price_mode === 'auto'}
                        onChange={e => setCommodity(key, { reference_price: e.target.value })} />
                    </label>
                    <label className="block text-sm text-gray-700">Adjustment to the looked-up price (%)
                      <input type="number" step="0.5" className={`${input} mt-1`} value={c.price_adjust_pct} onChange={e => setCommodity(key, { price_adjust_pct: Number(e.target.value) })} />
                    </label>
                  </div>
                  {c.reference_source && <p className="sm:col-span-2 text-xs text-gray-500 break-all">Source: {c.reference_source}</p>}
                  <label className="block text-sm text-gray-700">Product names (one per line)
                    <textarea className={`${input} mt-1`} rows={4} value={c.product_names} onChange={e => setCommodity(key, { product_names: e.target.value })} />
                  </label>
                  <label className="block text-sm text-gray-700">Origins: Country | Port | Incoterm
                    <textarea className={`${input} mt-1 font-mono`} rows={4} value={c.origins} onChange={e => setCommodity(key, { origins: e.target.value })} />
                  </label>
                  <label className="block text-sm text-gray-700">Specifications (one per line)
                    <textarea className={`${input} mt-1`} rows={4} value={c.specs} onChange={e => setCommodity(key, { specs: e.target.value })} />
                  </label>
                  <label className="block text-sm text-gray-700">Packing (one per line)
                    <textarea className={`${input} mt-1`} rows={4} value={c.packaging} onChange={e => setCommodity(key, { packaging: e.target.value })} />
                  </label>
                  <div className="sm:col-span-2 grid grid-cols-4 gap-3">
                    {[['min', 'Min quantity'], ['max', 'Max quantity'], ['step', 'Step'], ['unit', 'Unit']].map(([f, l]) => (
                      <label key={f} className="block text-sm text-gray-700">{l}
                        <input className={`${input} mt-1`} type={f === 'unit' ? 'text' : 'number'} value={c.quantity[f]}
                          onChange={e => setCommodity(key, { quantity: { ...c.quantity, [f]: f === 'unit' ? e.target.value : Number(e.target.value) } })} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 mt-4">
          {dirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
          <button onClick={save} disabled={saving || !dirty} className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-40" style={{ backgroundColor: primaryColor }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Run history</h3>
        {runs.length === 0 ? <p className="text-sm text-gray-500">No runs yet. Try a preview.</p> : (
          <div className="divide-y">
            {runs.map(r => (
              <div key={r._id} className="py-2">
                <button className="w-full flex flex-wrap items-center justify-between gap-2 text-left" onClick={() => setExpanded(expanded === r._id ? null : r._id)}>
                  <span className="flex items-center gap-3 text-sm">
                    <StatusBadge status={r.status} />
                    <span className="text-gray-800">{fmt(r.started_at)}</span>
                    <span className="text-gray-500">{r.trigger === 'schedule' ? 'scheduled' : 'manual'} · {r.mode}</span>
                  </span>
                  <span className="flex items-center gap-3 text-sm text-gray-500">
                    {r.offers?.length || 0} offers · ${(r.cost_usd || 0).toFixed(2)}
                    {expanded === r._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>
                {expanded === r._id && (
                  <div className="mt-2 ml-1 text-sm space-y-1 text-gray-600">
                    {r.error && <div className="text-red-700 bg-red-50 rounded p-2">{r.error}</div>}
                    {(r.steps || []).filter(s => s.status !== 'running').map((s, i) => (
                      <div key={i}>{s.status === 'failed' ? '❌' : s.status === 'warn' ? '⚠️' : '✅'} <strong>{s.name}</strong> — {s.detail}</div>
                    ))}
                    {(r.offers || []).map((o, i) => <div key={i} className="text-gray-800">• {o.title} — {o.price}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersTab;
